/**
 * The farm auction, as a state machine — turns `13a` and `13c`.
 *
 * The farm rounds are not a draft. Since 2021 a GM does not select a college player, he NOMINATES
 * one in serpentine order and then the room bids FAAB on him. So a turn has two halves that
 * belong to different people: the nomination is the nominator's, the sale is everybody's.
 *
 * `13c` draws the whole turn as ONE CARD in ONE SLOT that never moves or changes size — "the
 * worst thing a bidding UI can do is move the Raise button as somebody else raises" — showing one
 * of five things. This module decides WHICH of the five, for a given viewer, and nothing else.
 * Keeping that here rather than in the component is what lets the five states be tested, and the
 * distinction that matters most is the one the drawing spells out:
 *
 *   PASSING AND BEING BLOCKED ARE DIFFERENT THINGS. Pass is a deliberate exit that costs nothing.
 *   Blocked is not a choice — a GM at 3 of 3, or at his allowance for the year, gets the controls
 *   greyed with the reason stated, "because the alternative is a GM bidding for twenty seconds
 *   before the app tells him he was never eligible" (2017-II-5 §6).
 */

import { managerShort } from './identity.js';
import type { FarmNomination } from '../store/types.js';

export type AuctionState =
  /** Somebody else is choosing. Controls visible but inert, so the shape is already familiar. */
  | 'waiting'
  /** Your turn to nominate — the only state that changes colour, because it waits on you. */
  | 'nominate'
  /** Bidding open on a named player. */
  | 'bidding'
  /** Sold. Lingers with the price and the consequence before the next nomination opens. */
  | 'sold'
  /** Your farm or your allowance is full: not a choice, so the reason is stated. */
  | 'blocked'
  /** Every turn is done. */
  | 'over';

export interface Turn {
  round: number;
  slot: number;
  nominator: string;
  rec?: FarmNomination;
}

export interface AuctionView {
  state: AuctionState;
  /** The turn the card is about, or null when the auction is finished. */
  turn: Turn | null;
  /** Why the viewer cannot bid, when `blocked`. Stated on the card as a sentence. */
  blockedBecause?: string;
  /** The count behind it — `3 of 3 held` — which the card puts in the header, not the sentence. */
  blockedCount?: string;
  /** The standing high bid on the open nomination. */
  high: { gm: string; amount: number } | null;
  /** What the viewer would pay next: one more than the high bid, or the opening bid. */
  nextBid: number;
  /** True when the viewer already holds the high bid — the card says "you are high at $26". */
  youAreHigh: boolean;
}

export interface AuctionInput {
  turns: readonly Turn[];
  /** The manager looking at the card. */
  me: string;
  /** Farm players each GM holds, tonight's wins included. */
  heldBy: Readonly<Record<string, number>>;
  /** The cap on farm players held at once (2013-III-1 §4 as amended; config knob). */
  cap: number;
  /** FAAB the viewer has to spend. Used for the ceiling, not for eligibility. */
  faab?: number;
}

/** The first bid on a fresh nomination. A farm player has never gone for nothing. */
export const OPENING_BID = 1;

/**
 * Whether a GM may bid at all. Two independent blocks, both from 2017-II-5 §6: a full farm, and
 * an exhausted yearly allowance. The cap is the one this app can compute today — the allowance
 * needs a season's transactions and is checked at the point of sale.
 */
export function farmBlock(
  gm: string,
  heldBy: Readonly<Record<string, number>>,
  cap: number,
  wonThisRound: readonly string[] = [],
): { why: string; count: string } | null {
  const who = gm.toUpperCase();
  const held = heldBy[who] ?? 0;
  if (held >= cap) return { why: 'Your farm is full.', count: `${held} of ${cap} held` };
  /*
   * ONE WIN PER ROUND (Tim, 9 September: "the same rule restricts a GM from winning two
   * nominations in the same round. we allowed this to happen this year accidentally, but
   * programmatically disallow this going forward").
   *
   * 2025-proposal-04-farm-two-rounds §5 and §7: a GM may win "a maximum of one player in the first
   * round" and is "prohibited from making bids in any round of the Draft if they have previously
   * won an auction for a farm system player earlier in that same round". Two clauses, one fact —
   * the man is out of THIS round and back in the next one, which is what makes this different from
   * a full farm.
   */
  if (wonThisRound.some((g) => g.toUpperCase() === who)) {
    return { why: 'Already won this round.', count: 'one win per round' };
  }
  return null;
}

/** Everyone who has already won a lot in this round. 2025-proposal-04-farm-two-rounds §5. */
export function roundWinners(turns: readonly Turn[], round: number): string[] {
  const out = new Set<string>();
  for (const t of turns) {
    if (t.round !== round || !t.rec?.winner) continue;
    out.add(t.rec.winner.toUpperCase());
  }
  return [...out];
}

/**
 * A TURN NOBODY HAS OPENED YET — the one function that answers "whose nomination is next".
 *
 * EXPORTED, AND THE ONLY COPY (Tim, 9 September: "i can't get the farm pick rounds to reset fully
 * to john..."). There were three: this one, and two more written out by hand in `FarmDraft` as
 * `!t.rec`. Fixing the first left the screen contradicting itself — the bar read `John is
 * choosing 1.01` off this and the nominate card read `Nominating for Samant` off a copy — because
 * a turn can perfectly well have a record and still be unstarted, and only one of the three knew
 * it. `clearNomination` says so in its own name: it hands the turn back to its nominator
 * UNSTARTED and keeps the row. So after a reset, every turn anybody had ever opened has a row and
 * every turn nobody had is bare, and `!t.rec` walks past the first man to the first untouched one.
 *
 * `winner` and `passed` are the two ways a turn is genuinely finished, and both are checked rather
 * than assumed absent.
 */
export const unstarted = (t: Turn): boolean => (
  !t.rec || (!t.rec.player && !t.rec.winner && !t.rec.passed)
);

export function auctionView(
  { turns, me, heldBy, cap }: AuctionInput,
): AuctionView {
  const actor = me.toUpperCase();

  /*
   * The card is about the FIRST unfinished turn — the one being bid on if there is one, otherwise
   * the next nomination. A sold turn keeps the card for a moment on purpose (13c: "Sold lingers a
   * few seconds with the price and the consequence"), which the component times; here, a turn
   * with a winner is finished and the card moves on.
   */
  const open = turns.find((t) => t.rec && t.rec.player && !t.rec.winner && !t.rec.passed) ?? null;
  /*
   * AN UNSTARTED TURN IS ONE WITH NO PLAYER ON IT, NOT ONE WITH NO RECORD (Tim, 8 September:
   * "haven't started the farm rounds, but for some reason, it says samant will be the first
   * nomination? should be john").
   *
   * This used to ask `!t.rec`, and a turn can perfectly well have a record and still be unstarted:
   * `clearNomination` says so in its own name — "hand the turn back to its nominator, unstarted" —
   * and keeps the row, with `passed: false` and no player on it. So the moment a commissioner
   * pressed `Reset the farm round`, John's 1.01 had a row again, the search stepped over it, and
   * the card announced Samant's nomination on a board where nobody had nominated anything.
   *
   * `winner` and `passed` are the two ways a turn is genuinely finished, and both are checked
   * rather than assumed absent: a sold row also has no `player` cleared off it, and a passed
   * nomination is over whether or not anybody was ever named.
   */
  const turn = open ?? turns.find(unstarted) ?? null;

  const rec = turn?.rec;
  const high = rec?.highBidder && rec.highBid
    ? { gm: rec.highBidder.toUpperCase(), amount: rec.highBid }
    : null;
  const youAreHigh = high?.gm === actor;
  const nextBid = high ? high.amount + 1 : OPENING_BID;

  if (!turn) {
    return { state: 'over', turn: null, high: null, nextBid, youAreHigh: false };
  }

  const base = { turn, high, nextBid, youAreHigh };

  // Bidding is open on a named player.
  if (open) {
    const block = farmBlock(actor, heldBy, cap, roundWinners(turns, open.round));
    /*
     * Blocked outranks bidding, but NOT while you hold the high bid — a GM whose winning bid
     * would fill his farm is still in the auction for that player; the block applies to the next
     * one. Getting this backwards would grey the controls out from under the man about to win.
     */
    if (block && !youAreHigh) {
      return { ...base, state: 'blocked', blockedBecause: block.why, blockedCount: block.count };
    }
    return { ...base, state: 'bidding' };
  }

  // Nobody has nominated yet: whose turn is it?
  if (turn.nominator.toUpperCase() === actor) return { ...base, state: 'nominate' };
  return { ...base, state: 'waiting' };
}

/* -- the auction bar (13a desktop, 13b phone) -------------------------------------------------
 *
 * The drawing gives the farm auction the same dark bar the live draft has, in the same place, and
 * says so: "the clock strip keeps its position and its shape; only the label changes from ON THE
 * CLOCK to BIDDING OPEN, and the countdown now belongs to the sale rather than the turn."
 *
 * Two people matter per turn rather than one, so the bar names the NOMINATOR and the HIGH BIDDER
 * separately. And it is the only piece of chrome that changes colour, which it does for exactly one
 * reason — it is your nomination and the room is waiting on you.
 */

export type BarTone =
  /** A player is named and the room is bidding. */
  | 'open'
  /** Yours to name. The only coloured state. */
  | 'yours'
  /** Somebody else is choosing. */
  | 'waiting';

export interface AuctionBar {
  tone: BarTone;
  /** The word the tag carries — `BIDDING OPEN`, as drawn. */
  tag: string;
  /** The phone's whole sentence: `Amit nominated · 1.05`. Code stated separately for the desktop. */
  short: string;
  nominator: string;
  code: string;
  player?: string;
}

export function auctionBar(view: AuctionView, me: string): AuctionBar | null {
  const { turn } = view;
  if (!turn) return null;
  const code = nomCode(turn.round, turn.slot);
  const who = managerShort(turn.nominator);

  if (turn.rec?.player && !turn.rec.winner) {
    return {
      tone: 'open', tag: 'BIDDING OPEN', short: `${who} nominated · ${code}`,
      nominator: turn.nominator, code, player: turn.rec.player,
    };
  }
  if (turn.nominator.toUpperCase() === me.toUpperCase()) {
    return {
      tone: 'yours', tag: 'YOUR NOMINATION', short: `Your nomination · ${code}`,
      nominator: turn.nominator, code,
    };
  }
  return {
    tone: 'waiting', tag: 'NOMINATING', short: `${who} nominating · ${code}`,
    nominator: turn.nominator, code,
  };
}

/**
 * How many GMs are still live on the open lot — the phone's `4 still bidding`.
 *
 * Neither a pass nor a full farm counts, and they are different reasons: a pass is a decision, a
 * full farm is the rule (2017-II-5 §6). The phone has no room to say which, so it says the number
 * and shows the faces; the desktop rail lists both reasons in full.
 */
export function stillBidding(
  order: readonly string[],
  passedBy: readonly string[] | undefined,
  heldBy: Readonly<Record<string, number>>,
  cap: number,
  wonThisRound: readonly string[] = [],
): string[] {
  const passed = new Set((passedBy ?? []).map((g) => g.toUpperCase()));
  const won = new Set(wonThisRound.map((g) => g.toUpperCase()));
  return order.filter((g) => {
    const gm = g.toUpperCase();
    return !passed.has(gm) && !won.has(gm) && (heldBy[gm] ?? 0) < cap;
  });
}

/* -- the nominations grid (13a) ---------------------------------------------------------------
 *
 * The drawing makes the nominations the same object as the draft board at a different unit —
 * twenty nominations instead of a hundred and sixty picks — so it is ten columns, one row per
 * round, serpentine, and a sold cell carries the violet farm frame the board already uses.
 *
 * The one thing that is easy to get backwards, and which the drawing calls out in its own legend:
 * A SOLD CELL IS TINTED FOR THE WINNER, NOT THE NOMINATOR. The column says whose nomination it
 * was; the colour says who now owns the player. Those are different people most of the time, which
 * is the whole point of an auction, and tinting by column would make the grid say the opposite of
 * what happened.
 */

/** Which of the drawn cell states, plus `passed`, which the drawing has no data for. */
export type NomState = 'sold' | 'open' | 'passed' | 'upcoming';

export interface NomCell {
  round: number;
  slot: number;
  /** Whose nomination this turn is. The column. */
  nominator: string;
  /** `1.05` — the same shape the board's pick codes use. */
  code: string;
  state: NomState;
  player?: string;
  position?: string;
  /** Who won him, and for how much. The TINT comes from here. */
  winner?: string;
  bid?: number;
}

/**
 * Where a turn sits on the grid.
 *
 * Odd rounds run left to right, even rounds right to left — so round 2's slot 1 is the LAST
 * column. Derived from the round and slot rather than from the nominator's seat, because the
 * nominator can be missing from the order (a manager who left mid-season shows in an old log).
 */
export function nomColumn(round: number, slot: number, teams: number): number {
  return round % 2 === 1 ? slot - 1 : teams - slot;
}

export function nomCode(round: number, slot: number): string {
  return `${round}.${String(slot).padStart(2, '0')}`;
}

/**
 * The grid, one array per round, each already in COLUMN order — so the component renders rows
 * without knowing anything about the snake.
 */
export function nomGrid(turns: readonly Turn[], teams: number): (NomCell | null)[][] {
  const rounds = turns.reduce((n, t) => Math.max(n, t.round), 0);
  const rows: (NomCell | null)[][] = Array.from(
    { length: rounds },
    () => Array.from({ length: teams }, () => null),
  );

  for (const t of turns) {
    const col = nomColumn(t.round, t.slot, teams);
    if (col < 0 || col >= teams) continue;
    const rec = t.rec;
    const state: NomState = rec?.winner ? 'sold'
      : rec?.passed ? 'passed'
        : rec?.player ? 'open'
          : 'upcoming';
    rows[t.round - 1]![col] = {
      round: t.round,
      slot: t.slot,
      nominator: t.nominator,
      code: nomCode(t.round, t.slot),
      state,
      player: rec?.player,
      position: rec?.position,
      winner: rec?.winner,
      bid: rec?.bid,
    };
  }
  return rows;
}

/** The header's count — the drawing writes `2 rounds, serpentine — 4 of 20 sold`. */
export function nomSoldCount(turns: readonly Turn[]): { sold: number; total: number } {
  return { sold: turns.filter((t) => t.rec?.winner).length, total: turns.length };
}

/**
 * The turn that has just sold, if it sold recently enough to still be on the card. The component
 * passes `now`; the drawing's own dwell is a few seconds.
 */
export const SOLD_DWELL_MS = 6000;

export function justSold(
  turns: readonly Turn[],
  now: number,
  dwellMs = SOLD_DWELL_MS,
): Turn | null {
  const sold = turns
    .filter((t) => t.rec?.winner && t.rec.updatedAt)
    .sort((a, b) => (b.rec!.updatedAt > a.rec!.updatedAt ? 1 : -1))[0];
  if (!sold) return null;
  const at = Date.parse(sold.rec!.updatedAt);
  if (Number.isNaN(at)) return null;
  const age = now - at;
  /*
   * Bounded at BOTH ends. Ten browsers write these timestamps from ten unsynchronised clocks, and
   * a client running two minutes fast used to leave the sold card up for two minutes — the card
   * that is supposed to linger for six seconds sitting over the next nomination. A sale stamped
   * in the future is not fresher than one stamped now.
   */
  if (age < -dwellMs || age > dwellMs) return null;
  return sold;
}

/** Every raise button the drawing offers, and what each would cost. */
export function raiseSteps(nextBid: number, steps: readonly number[] = [1, 5, 10]) {
  return steps.map((s) => ({ step: s, to: nextBid - 1 + s }));
}

/**
 * What is left if you win at this price. The drawing states both numbers side by side because the
 * second is the one that decides the bid.
 */
export function leftIfYouWin(faab: number, bid: number): number {
  return Math.max(0, faab - bid);
}

/* -- two people with the same rights ---------------------------------------------------------
 *
 * Both the nominator and any commissioner may open a lot, so two clients can write the same
 * document. Firestore's last-write-wins would silently discard one of two DIFFERENT players named
 * at the same moment, which on draft night is the worst kind of bug: nobody sees it happen and the
 * board simply has the wrong man on it.
 *
 * There is no compare-and-set in the store's write path, so the mitigation is two-part, and these
 * functions are the part that can be reasoned about and tested: re-read the document immediately
 * before writing, then ask here whether the write is still legal. That narrows the race from the
 * whole nomination period to one round trip. It does not close it, which is why the guards below
 * are written to make the SURVIVING outcome the correct one wherever possible:
 *
 *   NOMINATE is refused outright if a player is already named. Two nominations cannot merge, so
 *   the second one must lose loudly rather than overwrite.
 *
 *   RAISE is monotonic. A raise is only written if it beats the standing bid, so two simultaneous
 *   raises resolve to the higher — which is exactly what an auction is supposed to do.
 *
 *   PASS merges. It appends to a list, and a lost append costs nothing but a name in the "who is
 *   bidding" column, so it is the one case where losing the race is harmless.
 */

export type Guard = { ok: true } | { ok: false; why: string };

const OK: Guard = { ok: true };

export function guardNominate(
  existing: FarmNomination | undefined,
  by: string,
  nominator: string,
  privileged: boolean,
): Guard {
  if (existing?.player) {
    return { ok: false, why: `${existing.player} has already been nominated for this turn.` };
  }
  if (existing?.passed) return { ok: false, why: 'This turn has already been passed.' };
  const isNominator = by.toUpperCase() === nominator.toUpperCase();
  if (!isNominator && !privileged) {
    return { ok: false, why: `It is ${nominator}'s nomination.` };
  }
  return OK;
}

/**
 * A raise, BY A NAMED BIDDER.
 *
 * The bidder was implicit before — whoever was pressing the button — and on the live site that is
 * always a commissioner, recording what the room calls out. So every bid in a real auction was
 * being written down as the commissioner's own, and the man who actually said "three" did not
 * appear anywhere. `by` fixes that, and it makes the second rule below expressible at all.
 *
 * NOBODY OUTBIDS HIMSELF. An auction where the standing high bidder can raise his own bid is not
 * an auction, it is a way to lose FAAB by mis-tapping — and with a going-gone clock that resets on
 * every raise, it is also a way to extend a lot you have already won. Refused outright.
 */
export function guardRaise(
  existing: FarmNomination | undefined,
  to: number,
  blocked: boolean,
  by?: string,
): Guard {
  if (!existing?.player) return { ok: false, why: 'Nothing is up for bid.' };
  if (existing.winner) return { ok: false, why: `Already sold to ${existing.winner}.` };
  if (blocked) return { ok: false, why: 'His farm is full.' };
  if (by && existing.highBidder
    && existing.highBidder.toUpperCase() === by.toUpperCase()) {
    return { ok: false, why: `${managerShort(by)} already holds the high bid.` };
  }
  const standing = existing.highBid ?? 0;
  if (to <= standing) {
    return { ok: false, why: `The bid is already ${standing}. Somebody got there first.` };
  }
  return OK;
}

/* -- putting a lot back ----------------------------------------------------------------------
 *
 * Draft night is a room with ten people talking over each other and one commissioner typing. The
 * write guards above stop two clients from corrupting a lot; these are for the other half of the
 * problem, which is a lot that is perfectly consistent and simply WRONG — the wrong player named,
 * a bid heard as thirty that was thirteen, a sale hammered while somebody still had his hand up.
 *
 * Every one of them is a commissioner action, and every one of them is reversible. Nothing here
 * deletes a turn: a cleared nomination is an unstarted turn again, which is a state the rest of
 * the app already understands.
 */

/** Wipe the bidding on an open lot, keeping the player up. Pair it with `withOpeningBid`. */
export function clearBids(n: FarmNomination): FarmNomination {
  const { highBid: _b, highBidder: _g, bidAt: _a, passedBy: _p, ...rest } = n;
  return { ...rest, updatedAt: new Date().toISOString() };
}

/**
 * NAMING A PLAYER IS A BID (Tim, 9 September: "when samant nominated nate frazier, he should have
 * nominated him with 1FAAB at stake and the recorded as the highest bidder at that point, but the
 * clock didn't move until I clicked the option presented to me to bid 1FAAB myself").
 *
 * 2019-II-6 §2 says the league, "including the proposing GM", begins the bidding on the man he
 * proposes. The app read that as "the proposing GM may bid like everyone else" and opened the lot
 * with nothing at stake, which is a lot nobody has bid on: the going-gone clock keys off `bidAt`
 * and there was no `bidAt`, so a nomination sat there until somebody else opened it — and whoever
 * did became the opener, which on Frazier meant the man who nominated him had no money on him and
 * the commissioner who was only starting the clock won him.
 *
 * So the nomination IS the opening bid, at ₣1, held by the nominator. The room bids from ₣2, the
 * clock starts the moment the name lands, and a lot that nobody wants goes to the man who wanted
 * it enough to name it.
 *
 * `opener` is null where he cannot hold it: 2025-proposal-01-faab-farm-bids §1 forbids a bid he
 * cannot pay, and 2019-II-6 §6 puts a GM with a full farm out of the bidding. Then the lot opens
 * with the
 * clock running and nothing at stake, and the room starts at ₣1 — he may still nominate, which is
 * §3's point, he just cannot be the one holding it.
 */
export function withOpeningBid(n: FarmNomination, opener: string | null): FarmNomination {
  const at = new Date().toISOString();
  /* `bidAt` either way: it is the LOT'S clock, and a lot with no bid on it is still on the clock. */
  if (!opener) return { ...n, bidAt: at, updatedAt: at };
  return {
    ...n, highBid: OPENING_BID, highBidder: opener.toUpperCase(), bidAt: at, updatedAt: at,
  };
}

/**
 * The lot nobody wanted.
 *
 * 2025-proposal-04-farm-two-rounds §7 puts a man who has already won this round out of the bidding,
 * so a lot he nominates can open with nobody able to take it at ₣1 — and Tim's ruling on what
 * happens then: "if no GMs bid $1, the nominee is not able to be brought up as a nominee by another
 * GM later, but is included in the prospects next year, if eligible."
 *
 * So the turn closes with the PLAYER STILL ON IT. That is the whole mechanism: every screen that
 * asks whether a prospect is available reads the nominated names out of the log, so a man named and
 * not sold is off the board for the rest of the evening without anything having to remember him
 * separately. Next year's bank is next year's file, and he is in it if he is still eligible.
 *
 * `passed` marks it finished — the same field a nominator's own pass sets, because to every clock
 * and count on the screen they are the same thing: a turn that is over and sold nothing.
 */
export function closeUnsold(n: FarmNomination): FarmNomination {
  const { highBid: _b, highBidder: _g, bidAt: _a, ...rest } = n;
  return { ...rest, passed: true, updatedAt: new Date().toISOString() };
}

/** Take the player back off and hand the turn back to its nominator, unstarted. */
export function clearNomination(n: FarmNomination): FarmNomination {
  return {
    season: n.season, round: n.round, slot: n.slot, nominator: n.nominator,
    passed: false, updatedAt: new Date().toISOString(),
  };
}

/**
 * Undo a sale. The player goes back up for bid at the price that won him.
 *
 * AND THE GOING-GONE CLOCK STARTS AGAIN, which is the difference between this working and this
 * appearing to do nothing (Tim, 9 September: "I tried to undo, but the screen would only flash and
 * not actually undo the action until I paused"). A sale happens because the clock LAPSED, so the
 * moment the sale came off, the lot underneath it had an expired clock and a standing high bid —
 * and the auto-hammer, which is watching for exactly that, sold it again on the next tick. Undo
 * wrote, the hammer wrote back, and the screen flickered between the two. Pausing stopped the
 * hammer, which is why pausing "fixed" it.
 *
 * Putting a lot back has to mean putting the room back where it was before the clock ran out, so
 * the clock is reset with it and the bidding has somewhere to go.
 */
export function undoSale(n: FarmNomination): FarmNomination {
  const { winner: _w, bid: _b, ...rest } = n;
  const at = new Date().toISOString();
  return { ...rest, ...(rest.player ? { bidAt: at } : {}), updatedAt: at };
}

/** Record that a GM is out of this one. Merges, and never twice. */
export function withPass(n: FarmNomination, gm: string): FarmNomination {
  const who = gm.toUpperCase();
  const already = n.passedBy ?? [];
  if (already.some((g) => g.toUpperCase() === who)) return n;
  return { ...n, passedBy: [...already, who], updatedAt: new Date().toISOString() };
}

/** And take it back, for the man who says "no, wait". */
export function withoutPass(n: FarmNomination, gm: string): FarmNomination {
  const who = gm.toUpperCase();
  return {
    ...n,
    passedBy: (n.passedBy ?? []).filter((g) => g.toUpperCase() !== who),
    updatedAt: new Date().toISOString(),
  };
}

export function guardSell(existing: FarmNomination | undefined): Guard {
  if (!existing?.player) return { ok: false, why: 'Nothing is up for bid.' };
  if (existing.winner) return { ok: false, why: `Already sold to ${existing.winner}.` };
  if (!existing.highBidder || existing.highBid == null) {
    return { ok: false, why: 'Nobody has bid yet.' };
  }
  return OK;
}
